import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import * as XLSX from 'xlsx';

import { SelectionOption } from '../form-items/select/select.component';
import {
  detectHasHeader,
  detectSeparator,
  detectSheetHasHeader,
  hasSupportedExtension,
  isSpreadsheet,
  looksBinary,
  parseCodeFile,
  parseSheetRows,
  toLines,
} from './code-file-import.util';

/**
 * Both code imports (subscription card batches, discount codes) now read the upload through the same
 * ExcelDataReader setup and honour the same `FileConfig*` fields, so the dialog needs nothing beyond
 * the file to know what to offer.
 */
export interface CodeFileImportDialogData {
  file: File;
}

/** What the dialog decided; the caller turns this into the endpoint's `FileConfig*` fields. */
export interface CodeFileImportConfig {
  has_header: boolean;
  separator: string;
  column_index: number;
  column_name?: string | null;
  encoding?: string | null;
}

// Only the head of the file is read for the preview - a code list is normally a few KB, and this
// keeps a mistakenly picked 200 MB file from being pulled into memory.
const PREVIEW_BYTE_LIMIT = 2 * 1024 * 1024;
const PREVIEW_ROWS = 5;

@Component({
  selector: 'lib-code-file-import-dialog',
  templateUrl: './code-file-import-dialog.component.html',
  styleUrls: ['./code-file-import-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CodeFileImportDialogComponent {
  // Tab is deliberately absent: ASP.NET Core turns a whitespace-only form value into null
  // (ConvertEmptyStringToNull), so a tab arrives as null and the parser falls back to a comma.
  readonly separatorOptions: SelectionOption[] = [
    { key: 'global.code_import.separator_comma', value: ',' },
    { key: 'global.code_import.separator_semicolon', value: ';' },
    { key: 'global.code_import.separator_pipe', value: '|' },
  ];

  // .NET encoding names, which is what the server passes to Encoding.GetEncoding.
  readonly encodingOptions: SelectionOption[] = [
    { key: 'global.code_import.encoding_auto', value: '' },
    { key: 'global.code_import.encoding_utf8', value: 'utf-8' },
    { key: 'global.code_import.encoding_1252', value: 'windows-1252' },
    { key: 'global.code_import.encoding_1250', value: 'windows-1250' },
    { key: 'global.code_import.encoding_8859', value: 'iso-8859-1' },
  ];

  loading = true;
  error: string | null = null;
  /** The file was larger than the preview limit, so the row count below is only what was read. */
  truncated = false;
  /** A spreadsheet: the separator and encoding do not apply to it. */
  isSheet = false;

  hasHeader = false;
  separator = ',';
  encoding = '';
  columnIndex = 0;

  headerCells: string[] | null = null;
  dataRows: string[][] = [];
  columnCount = 1;

  // Derived once per parse rather than per change-detection pass: these are bound to @Inputs
  // (`[options]`, `*ngFor`), and handing those a freshly built array every check is both wasted work
  // and an ExpressionChangedAfterItHasBeenChecked in dev builds.
  columnOptions: SelectionOption[] = [];
  previewRows: string[][] = [];
  hiddenRowCount = 0;
  /** Rows the import will actually take, which is what the button promises. */
  importableRowCount = 0;

  private lines: string[] = [];
  private sheetRows: string[][] = [];

  constructor(
    public dialogRef: MatDialogRef<CodeFileImportDialogComponent, CodeFileImportConfig>,
    @Inject(MAT_DIALOG_DATA) public data: CodeFileImportDialogData,
    private transloco: TranslocoService
  ) {
    this.read(data.file);
  }

  get fileSizeLabel(): string {
    const kb = this.data.file.size / 1024;

    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  /** CSV only: the workbook reader ignores it. */
  get showEncoding(): boolean {
    return !this.isSheet;
  }

  get canImport(): boolean {
    return !this.loading && !this.error && this.importableRowCount > 0;
  }

  onSeparatorChange(separator: string): void {
    this.separator = separator;
    this.refresh();
  }

  // The encoding changes the bytes-to-text step, so the file has to be read again.
  onEncodingChange(encoding: string): void {
    this.encoding = encoding;
    this.loading = true;
    this.readText(this.data.file);
  }

  onHasHeaderChange(hasHeader: boolean): void {
    this.hasHeader = hasHeader;
    this.refresh();
  }

  onColumnChange(columnIndex: number): void {
    this.columnIndex = columnIndex;
    this.countImportableRows();
  }

  submit(): void {
    if (!this.canImport) return;

    this.dialogRef.close({
      has_header: this.hasHeader,
      separator: this.separator,
      column_index: this.columnIndex,
      column_name: this.headerCells?.[this.columnIndex]?.trim() || null,
      encoding: this.isSheet ? null : this.encoding || null,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private read(file: File): void {
    if (!file.size) {
      this.error = 'global.code_import.error_empty';
      this.loading = false;
      return;
    }

    if (!hasSupportedExtension(file.name)) {
      this.error = 'global.code_import.error_unsupported_type';
      this.loading = false;
      return;
    }

    this.isSheet = isSpreadsheet(file.name);
    this.truncated = !this.isSheet && file.size > PREVIEW_BYTE_LIMIT;

    if (this.isSheet) {
      this.readSheet(file);
      return;
    }

    this.readText(file);
  }

  private readText(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      const text = String(reader.result ?? '');

      // A workbook renamed .csv reaches the CSV reader on both sides and yields nonsense rows.
      if (looksBinary(text)) {
        this.error = 'global.code_import.error_unsupported_type';
        this.loading = false;
        return;
      }

      this.lines = toLines(text);
      this.separator = detectSeparator(this.lines);
      this.hasHeader = detectHasHeader(this.lines, this.separator);
      this.refresh();
      this.loading = false;
    };

    reader.onerror = () => {
      this.error = 'global.code_import.error_unreadable';
      this.loading = false;
    };

    const slice = this.truncated ? file.slice(0, PREVIEW_BYTE_LIMIT) : file;

    // An explicit encoding is honoured here too, so the preview shows what the server will read.
    if (this.encoding) {
      reader.readAsText(slice, this.encoding);
    } else {
      reader.readAsText(slice);
    }
  }

  private readSheet(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const workbook = XLSX.read(new Uint8Array(reader.result as ArrayBuffer), { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // The server reads the first sheet as a grid, so the preview does the same: raw cells, no
        // header handling here, and every value as text.
        this.sheetRows = sheet
          ? (XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '', raw: false }) as string[][])
          : [];

        this.hasHeader = detectSheetHasHeader(this.sheetRows);
        this.refresh();
      } catch {
        this.error = 'global.code_import.error_unreadable';
      }

      this.loading = false;
    };

    reader.onerror = () => {
      this.error = 'global.code_import.error_unreadable';
      this.loading = false;
    };

    reader.readAsArrayBuffer(file);
  }

  private refresh(): void {
    const parsed = this.isSheet
      ? parseSheetRows(this.sheetRows, this.hasHeader)
      : parseCodeFile(this.lines, this.separator, this.hasHeader);

    this.headerCells = parsed.headerCells;
    this.dataRows = parsed.dataRows;
    this.columnCount = parsed.columnCount;

    if (this.columnIndex >= this.columnCount) {
      this.columnIndex = 0;
    }

    // A header name is the file's own word and is shown as-is; only an unnamed column is labelled.
    this.columnOptions = Array.from({ length: this.columnCount }, (_, i) => ({
      key: this.headerCells?.[i]?.trim() || this.transloco.translate('global.code_import.column_n', { n: i + 1 }),
      value: i,
    }));

    this.previewRows = this.dataRows.slice(0, PREVIEW_ROWS);
    this.hiddenRowCount = Math.max(0, this.dataRows.length - PREVIEW_ROWS);
    this.countImportableRows();
  }

  // The server drops a row whose chosen cell is blank without counting it anywhere, so counting
  // those here would promise an import size the summary then contradicts.
  private countImportableRows(): void {
    this.importableRowCount = this.dataRows.reduce((n, row) => (row[this.columnIndex]?.trim() ? n + 1 : n), 0);
  }
}
